import { map } from 'lodash';
import { ValidationSchema, Tuple } from '../../../types';
import { SupportingActivityItemModel, ActivityRelationItemModel } from '../../../api/models';
import nameValidator from './name';
import entityNameValidator from './entityName';
import { activityRelationSchema, activityRelationSoftSchema } from './activityRelationItem';
import {
    textMandatory, textAreaMax, mandatoryArrayWithCondition, textAreaBetweenMinAndMax, fieldRequiredValidatorConditional,
    maxLength, matchAlphanumericExtended, isABNUnique, radioButtonMandatoryWithCondition,
} from '../../validationFunctions';
import * as validatorRules from '../../validatorRules.json';
import { durationValidatorSoft, durationValidatorHard, durationValidatorWarning } from './duration';
import { maxCurrencyLength, mandatoryCurrency } from '../../validationFunctions/commonCurrencyInput';
import { mandatoryString, mandatoryYesNo } from './../../util';
import { interpolateValidationRule } from '../../validatorMessages';

const ArrayHasEntry = (value: any | undefined | null) => value !== undefined && value.length > 0;

const excludedRequired = (values: Partial<SupportingActivityItemModel>) => {
    return values.applicationHasIncludedExcludedActivities !== undefined && values.applicationHasIncludedExcludedActivities === 'Yes';
};

const isSupportCoreActivity = (values: Partial<SupportingActivityItemModel>) => {
    return values.supportCoreActivity !== undefined && values.supportCoreActivity === 'Yes';
};

export const activityPerformedBy123 = (values: Partial<SupportingActivityItemModel>) => {
    return values.activityPerformedBy !== undefined && (values.activityPerformedBy === 'OnlyTheRnDCompany' || values.activityPerformedBy === 'RnDCompanyAndOthers' || values.activityPerformedBy === 'EitherASubsidiary');
};

export const activityPerformedBy234 = (values: Partial<SupportingActivityItemModel>) => {
    return values.activityPerformedBy === 'RnDCompanyAndOthers' || values.activityPerformedBy === 'EitherASubsidiary' || values.activityPerformedBy === 'OnlyOthers';
};

const activityRelations = (values: Partial<SupportingActivityItemModel>) => (items: Partial<ActivityRelationItemModel>[] | undefined): any =>
    map(items, activityRelationSchema(values));

const activityRelationsSoft = (values: Partial<SupportingActivityItemModel>) => (items: Partial<ActivityRelationItemModel>[] | undefined): any =>
    map(items, activityRelationSoftSchema(values));

const validateSubsidiary = (
    values: Partial<SupportingActivityItemModel>,
    condition?: () => boolean): Tuple<(value: any) => boolean, string>[] => {
    if (!condition || condition()) {
        return [
            [value => isABNUnique(value, values.afiRnDEntityABN), validatorRules.BR169_Subsidiary_RnDEntity_Unique.FieldMessage],
            [value => isABNUnique(value, values.primaryTaxAgentAbn), validatorRules.BR171_Subsidiary_TaxAgent_Unique.FieldMessage],
            [value => isABNUnique(value, values.organisationUhcAbn), validatorRules.BR173_Subsidiary_UHCDetails_Unique.FieldMessage],
        ];
    }
    return [];
};

export const supportingActivitiesWarning: ValidationSchema<Partial<SupportingActivityItemModel>> = (values: Partial<SupportingActivityItemModel>) => {
    return {
        duration: durationValidatorWarning(values),
    };
};

export const radioButtonMandatory: Tuple<(value: any) => boolean, string>[] = [
    [textMandatory, validatorRules.BR1_Mandatory_Field.FieldMessage],
];

export const otherOrganisationRequired = (values: Partial<SupportingActivityItemModel>) => {
    return ArrayHasEntry(values.crcItems) || ArrayHasEntry(values.levyCollectingRspItems) || ArrayHasEntry(values.rspNonLevyItems);
};

const otherOrganisation134 = (values: Partial<SupportingActivityItemModel>) => {
    return values.otherOrganisation === 'YesRSP' || values.otherOrganisation === 'YesCRC' || values.otherOrganisation === 'No';
};

const isHeadMecGroup = (values: Partial<SupportingActivityItemModel>) => {
    return values.organisationMecType !== undefined && values.organisationMecType === 'HeadMecGroup';
};

const activityConductedBySubsidiary = (values: Partial<SupportingActivityItemModel>) => {
    return ['Subsidiary'].includes(values.activityConductedBy!);
};

export const otherOrganisation123 = (values: Partial<SupportingActivityItemModel>) => {
    return values.otherOrganisation === 'YesRSP' || values.otherOrganisation === 'YesRSPLevy' || values.otherOrganisation === 'YesCRC';
};

export const supportingActivitiesSoft: ValidationSchema<Partial<SupportingActivityItemModel>> = (values: Partial<SupportingActivityItemModel>) => {
    return {
        otherOrganisation: radioButtonMandatory,
        supportForCoreActivities: mandatoryString(() => otherOrganisation134(values)),
        activityPerformedBy: radioButtonMandatoryWithCondition(() => otherOrganisation134(values)),
        nameRNDEntityPerformingSupportingActivity: mandatoryString(() => activityPerformedBy234(values) && otherOrganisation134(values)),
        isEntityWhoWillConductSupportingConnected: mandatoryYesNo(() => activityPerformedBy234(values) && otherOrganisation134(values)),
        isSupportingActivityConductedUnderAgreementOutsideAus: mandatoryYesNo(() => activityPerformedBy234(values) &&
            otherOrganisation134(values)),
        activityConductedBy: radioButtonMandatoryWithCondition(() => isHeadMecGroup(values) &&
            otherOrganisation134(values) && activityPerformedBy123(values)),
        subsidiaryConductingActivity: fieldRequiredValidatorConditional(() => isHeadMecGroup(values)
            && otherOrganisation134(values) && activityConductedBySubsidiary(values) && activityPerformedBy123(values)),
        otherOrganisationName: mandatoryString(() => otherOrganisation123(values)),
        briefDescription: mandatoryString(() => otherOrganisation123(values)),
        totalExpenditure: mandatoryCurrency(() => otherOrganisation134(values)),
        excluded: mandatoryString(() => otherOrganisation134(values) && excludedRequired(values)),
        relatedGoodsAndServices: mandatoryString(() => otherOrganisation134(values)),
        supportCoreActivity: mandatoryString(() => otherOrganisation134(values)),
        explainWhyHowSupportingActivityUndertaken: mandatoryString(() => otherOrganisation134(values) && isSupportCoreActivity(values)),
        evidenceAvailable: mandatoryArrayWithCondition(() => otherOrganisation134(values)),
        isIntentionPreviouslyRegisteredSupportingRNDActivityApplication: mandatoryYesNo(),
        activityRelations: activityRelationsSoft(values),
        duration: durationValidatorSoft(values),
        actionsStepsCompleted: mandatoryString(),
    };
};

export const supportingActivities: ValidationSchema<Partial<SupportingActivityItemModel>> = (values: Partial<SupportingActivityItemModel>) => {
    return {
        name: nameValidator(values),
        subsidiaryConductingActivity: validateSubsidiary(values, () => values.activityConductedBy === 'Subsidiary'),
        activityRelations: activityRelations(values),
        briefDescription: textAreaBetweenMinAndMax('none', 'briefDescription', 50, 2000),
        supportForCoreActivities: textAreaBetweenMinAndMax('none', 'supportForCoreActivities', 2, 4000),
        explainWhyHowSupportingActivityUndertaken: textAreaBetweenMinAndMax('none', 'explainWhyHowSupportingActivityUndertaken', 2, 4000),
        duration: durationValidatorHard(values),
        totalExpenditure: [maxCurrencyLength(12)],
        actionsStepsCompleted: textAreaBetweenMinAndMax('none', 'actionsStepsCompleted', 2, 4000),
        evidenceAvailable: textAreaBetweenMinAndMax('none', 'actionsStepsCompleted', 50, 4000),
        nameRNDEntityPerformingSupportingActivity: entityNameValidator(values),
    };
};
